import React, { useState, useEffect } from 'react';
import {
  X,
  MapPin,
  Navigation,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  Home,
  Compass
} from 'lucide-react';
import {
  BhilaiLocationItem,
  detectLiveGeolocation,
  saveUserLocation,
  UserLocationState,
  HARI_RAJ_HUB,
  getSavedCustomerAddresses,
  SavedCustomerAddress,
  findClosestLocality,
  getOperationalAreas,
  OPERATIONAL_AREAS_CHANGED_EVENT
} from '../services/locationService';

interface LocationPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLocationSelected: (loc: UserLocationState) => void;
  currentLocation: UserLocationState | null;
}

export const LocationPickerModal: React.FC<LocationPickerModalProps> = ({
  isOpen,
  onClose,
  onLocationSelected,
  currentLocation
}) => {
  const [isDetectingGps, setIsDetectingGps] = useState(false);
  const [gpsError, setGpsError] = useState('');
  const [savedAddresses, setSavedAddresses] = useState<SavedCustomerAddress[]>([]);
  const [availableAreas, setAvailableAreas] = useState<BhilaiLocationItem[]>(() => getOperationalAreas());

  useEffect(() => {
    const handleAreasUpdate = () => {
      setAvailableAreas(getOperationalAreas());
    };
    window.addEventListener(OPERATIONAL_AREAS_CHANGED_EVENT, handleAreasUpdate);
    return () => window.removeEventListener(OPERATIONAL_AREAS_CHANGED_EVENT, handleAreasUpdate);
  }, []);

  // Body scroll lock on mobile when modal is open to ensure flawless scrolling
  useEffect(() => {
    if (isOpen) {
      setAvailableAreas(getOperationalAreas());
      setSavedAddresses(getSavedCustomerAddresses());
      setGpsError('');
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // 1. "Use your location" (GPS detection against Hari Raj 5 km hub)
  const handleUseGps = async () => {
    setIsDetectingGps(true);
    setGpsError('');

    const res = await detectLiveGeolocation();
    setIsDetectingGps(false);

    if (res.success && res.lat !== undefined && res.lng !== undefined) {
      const closest = findClosestLocality(res.lat, res.lng);
      const isServ = res.isServiceable !== undefined ? res.isServiceable : closest.isServiceable;
      const dist = res.distanceKm !== undefined ? res.distanceKm : closest.distanceKm;

      const newLoc: UserLocationState = {
        status: isServ ? 'serviceable' : 'unserviceable',
        areaName: isServ ? closest.name : 'Others',
        lat: res.lat,
        lng: res.lng,
        distanceKm: dist,
        googleMapsUrl: res.googleMapsUrl,
        nearestHubName: HARI_RAJ_HUB.name,
        source: 'gps',
        updatedAt: new Date().toISOString()
      };

      saveUserLocation(newLoc);
      onLocationSelected(newLoc);
      onClose();
    } else {
      setGpsError(res.error || 'Unable to detect location. Please select your colony below.');
    }
  };

  // 2. Select from Available Bhilai Areas or "Others (Coming Soon)"
  const handleSelectArea = (item: BhilaiLocationItem) => {
    const isServ = item.isServiceable;
    const newLoc: UserLocationState = {
      status: isServ ? 'serviceable' : 'unserviceable',
      areaName: item.name,
      lat: item.lat,
      lng: item.lng,
      nearestHubName: HARI_RAJ_HUB.name,
      source: 'manual',
      updatedAt: new Date().toISOString()
    };

    saveUserLocation(newLoc);
    onLocationSelected(newLoc);
    onClose();
  };

  // 3. Select Saved Address (at last)
  const handleSelectSavedAddress = (addr: SavedCustomerAddress) => {
    const newLoc: UserLocationState = {
      status: addr.isServiceable ? 'serviceable' : 'unserviceable',
      areaName: addr.area,
      lat: addr.lat,
      lng: addr.lng,
      googleMapsUrl: addr.googleMapsUrl,
      nearestHubName: HARI_RAJ_HUB.name,
      source: 'manual',
      updatedAt: new Date().toISOString()
    };

    saveUserLocation(newLoc);
    onLocationSelected(newLoc);
    onClose();
  };

  const isCurrentUnserviceable = currentLocation?.status === 'unserviceable';

  return (
    <div className="location-modal-overlay" onClick={onClose}>
      <div
        className="location-modal-card amazon-flipkart-style"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="loc-modal-title"
      >
        {/* Mobile Drag Indicator Handle */}
        <div className="location-bottom-sheet-handle" />

        {/* Header */}
        <div className="location-modal-header">
          <div className="location-header-left">
            <div className="location-icon-circle">
              <Compass size={20} color="var(--blishka-berry)" />
            </div>
            <div>
              <h3 id="loc-modal-title" className="location-modal-title">
                Choose your location
              </h3>
            </div>
          </div>

          <button
            type="button"
            className="location-modal-close"
            onClick={onClose}
            aria-label="Close location picker"
          >
            <X size={18} />
          </button>
        </div>

        <div className="location-modal-body mobile-scroll-container">
          {/* Out of 5 km Zone Short Red Strip */}
          {isCurrentUnserviceable && (
            <div className="loc-out-of-area-red-alert">
              <span className="loc-red-dot">🔴</span>
              <span className="loc-red-title">
                Doorstep parlour care in your Area is Coming Soon!
              </span>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════
              1. USE YOUR LOCATION (TOP)
             ══════════════════════════════════════════════════════ */}
          <div className="loc-top-action-wrap">
            <button
              type="button"
              onClick={handleUseGps}
              disabled={isDetectingGps}
              className="gps-detect-btn modern-action"
            >
              <div className="gps-btn-icon-wrap">
                <Navigation size={18} className={isDetectingGps ? 'spin-pulse' : ''} />
              </div>
              <div className="gps-btn-text-wrap">
                <span className="gps-btn-main">
                  {isDetectingGps ? 'Detecting your GPS location...' : 'Use your location'}
                </span>
                <span className="gps-btn-sub">Detect doorstep address via GPS</span>
              </div>
              <ChevronRight size={16} className="gps-btn-arrow" />
            </button>

            {gpsError && (
              <div className="location-gps-error">
                <AlertCircle size={14} />
                <span>{gpsError}</span>
              </div>
            )}
          </div>

          {/* ══════════════════════════════════════════════════════
              2. AVAILABLE AREAS LIST (BELOW IT)
             ══════════════════════════════════════════════════════ */}
          <div className="loc-available-areas-section">
            <div className="loc-section-title">
              <MapPin size={14} />
              <span>Available Areas (Bhilai)</span>
            </div>

            <div className="loc-areas-clean-list">
              {availableAreas.map((item) => {
                const isSelected =
                  currentLocation?.areaName &&
                  (currentLocation.areaName.toLowerCase() === item.name.toLowerCase() ||
                    currentLocation.areaName.toLowerCase().includes(item.name.toLowerCase()));

                return (
                  <div
                    key={item.id}
                    onClick={() => handleSelectArea(item)}
                    className={`colony-select-item ${isSelected ? 'selected' : ''} ${
                      !item.isServiceable ? 'is-coming-soon-item' : ''
                    }`}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="colony-item-left">
                      <MapPin
                        size={15}
                        className={`colony-pin-icon ${!item.isServiceable ? 'is-red-pin' : ''}`}
                      />
                      <span className="colony-name">{item.name}</span>
                    </div>

                    <div className="colony-item-right">
                      {item.isServiceable ? (
                        <span className="loc-badge-available">
                          <CheckCircle2 size={11} />
                          <span>Available</span>
                        </span>
                      ) : (
                        <span className="loc-badge-comingsoon">
                          <AlertCircle size={11} />
                          <span>Coming Soon</span>
                        </span>
                      )}
                      <ChevronRight size={14} className="colony-select-arrow" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ══════════════════════════════════════════════════════
              3. SAVED ADDRESSES TAB (AT LAST)
             ══════════════════════════════════════════════════════ */}
          {savedAddresses.length > 0 && (
            <div className="loc-saved-addresses-section">
              <div className="loc-section-title">
                <Home size={14} />
                <span>Saved Addresses</span>
              </div>

              <div className="loc-saved-addresses-list">
                {savedAddresses.map((addr) => {
                  const isMatch =
                    currentLocation?.areaName &&
                    addr.area &&
                    (currentLocation.areaName.toLowerCase().includes(addr.area.toLowerCase()) ||
                      addr.area.toLowerCase().includes(currentLocation.areaName.toLowerCase()));

                  return (
                    <div
                      key={addr.id}
                      onClick={() => handleSelectSavedAddress(addr)}
                      className={`loc-saved-item ${isMatch ? 'selected' : ''}`}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="loc-radio-circle">
                        {isMatch && <div className="loc-radio-inner" />}
                      </div>

                      <div className="loc-saved-details">
                        <div className="loc-saved-name-row">
                          <span className="loc-saved-name">{addr.clientName}</span>
                          {addr.phone && <span className="loc-saved-phone">· +91 {addr.phone}</span>}
                          {addr.isServiceable ? (
                            <span className="loc-badge-available">Available</span>
                          ) : (
                            <span className="loc-badge-comingsoon">Coming Soon</span>
                          )}
                        </div>
                        <p className="loc-saved-address-text">
                          {addr.address}, {addr.area}
                          {addr.pincode ? ` (${addr.pincode})` : ''}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
